/**
 * Operations Page
 * Four tabs: System Health, API Errors, Client Errors, Alert Rules
 */

import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { getErrorLogs, clearErrorLogs } from "../utils/errorLogger";
import type { ErrorLog } from "../utils/errorLogger";
import { formatDate, formatTime } from "../utils/formatters";
import Card from "../components/ui/Card";
import SmsRetentionPanel from "../components/admin/SmsRetentionPanel";
import {
  fetchApiErrors,
  fetchApiErrorStats,
  fetchDistinctEndpoints,
  fetchFunctionHealth,
  fetchWebhookLiveness,
  fetchSlowestFunctions,
  type ApiErrorLog,
  type ApiErrorFilters,
  type ApiErrorStats,
  type FunctionHealth,
  type WebhookLiveness,
  type SlowestFunction,
} from "../services/errorLogs.service";
import {
  fetchAlertRules,
  createAlertRule,
  updateAlertRule,
  deleteAlertRule,
  fetchActiveAlerts,
  acknowledgeAlert,
  resolveAlert,
  triggerAlertEvaluation,
  type AlertRule,
  type SystemAlert,
  type AlertRuleInput,
  type EvaluationResult,
} from "../services/alerts.service";

type Tab = "health" | "api" | "client" | "alerts";

// ============================================================================
// SYSTEM HEALTH TAB
// ============================================================================

const StatusDot: React.FC<{ status: "green" | "yellow" | "red" }> = ({
  status,
}) => {
  const colors = {
    green: "bg-green-500",
    yellow: "bg-yellow-500",
    red: "bg-red-500",
  };
  return (
    <span
      className={`inline-block w-2.5 h-2.5 rounded-full ${colors[status]}`}
    />
  );
};

function timeAgo(dateStr: string | null): string {
  if (!dateStr) {
    return "Never";
  }
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) {
    return "Just now";
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const SystemHealthTab: React.FC = () => {
  const [health, setHealth] = useState<FunctionHealth[]>([]);
  const [webhooks, setWebhooks] = useState<WebhookLiveness[]>([]);
  const [slowest, setSlowest] = useState<SlowestFunction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [h, w, s] = await Promise.all([
          fetchFunctionHealth(),
          fetchWebhookLiveness(),
          fetchSlowestFunctions(),
        ]);
        setHealth(h);
        setWebhooks(w);
        setSlowest(s);
      } catch (err) {
        console.error("Failed to load system health:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-gray-500">Loading system health...</div>
      </div>
    );
  }

  if (health.length === 0) {
    return (
      <Card padding="lg">
        <div className="text-center py-12">
          <p className="text-gray-500">
            No function call data yet. Data will appear once edge functions are
            invoked.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div>
      {/* Webhook Liveness Panel */}
      {webhooks.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
            Webhook Liveness
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {webhooks.map((wh) => (
              <Card key={wh.functionName} padding="md">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <StatusDot status={wh.status} />
                    <span className="font-mono text-sm text-gray-900">
                      {wh.functionName}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {wh.lastReceived
                      ? `Last received: ${timeAgo(wh.lastReceived)}`
                      : "No data"}
                  </div>
                </div>
                {wh.status === "red" && (
                  <div className="mt-2 text-xs text-red-600">
                    No webhook received in 48+ hours
                  </div>
                )}
                {wh.status === "yellow" && (
                  <div className="mt-2 text-xs text-yellow-600">
                    No webhook received in 24+ hours
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Function Health Grid */}
      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
        Function Health
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {health.map((fn) => (
          <Card key={fn.functionName} padding="md">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <StatusDot status={fn.status} />
                <span className="font-mono text-sm font-medium text-gray-900">
                  {fn.functionName}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <div className="text-gray-500">Today</div>
              <div className="text-gray-900 font-medium text-right">
                {fn.callsToday}
              </div>
              <div className="text-gray-500">This week</div>
              <div className="text-gray-900 font-medium text-right">
                {fn.callsThisWeek}
              </div>
              <div className="text-gray-500">Error rate</div>
              <div
                className={`font-medium text-right ${fn.errorRate > 10 ? "text-red-600" : fn.errorRate > 2 ? "text-yellow-600" : "text-green-600"}`}
              >
                {fn.errorRate}%
              </div>
              <div className="text-gray-500">Avg response</div>
              <div className="text-gray-900 font-medium text-right">
                {fn.avgResponseTimeMs}ms
              </div>
              <div className="text-gray-500">Last invoked</div>
              <div className="text-gray-900 text-right text-xs">
                {timeAgo(fn.lastInvoked)}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Slowest Functions */}
      {slowest.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
            Slowest Functions (This Week)
          </h3>
          <Card padding="md">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 text-gray-500 font-medium">
                    Function
                  </th>
                  <th className="text-right py-2 text-gray-500 font-medium">
                    Avg Response
                  </th>
                  <th className="text-right py-2 text-gray-500 font-medium">
                    Calls
                  </th>
                </tr>
              </thead>
              <tbody>
                {slowest.map((fn) => (
                  <tr
                    key={fn.functionName}
                    className="border-b border-gray-100"
                  >
                    <td className="py-2 font-mono text-gray-900">
                      {fn.functionName}
                    </td>
                    <td className="py-2 text-right text-gray-900">
                      {fn.avgResponseTimeMs}ms
                    </td>
                    <td className="py-2 text-right text-gray-500">
                      {fn.callCount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// API ERRORS TAB
// ============================================================================

const ApiErrorsTab: React.FC = () => {
  const [errors, setErrors] = useState<ApiErrorLog[]>([]);
  const [stats, setStats] = useState<ApiErrorStats | null>(null);
  const [endpoints, setEndpoints] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 25;

  // Filters
  const [endpointFilter, setEndpointFilter] = useState("");
  const [statusCodeFilter, setStatusCodeFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const filters: ApiErrorFilters = {};
      if (endpointFilter) {
        filters.endpoint = endpointFilter;
      }
      if (statusCodeFilter) {
        filters.statusCode = parseInt(statusCodeFilter, 10);
      }
      if (dateFrom) {
        filters.dateFrom = dateFrom;
      }
      if (dateTo) {
        filters.dateTo = dateTo;
      }

      const [errorsResult, statsResult, endpointsResult] = await Promise.all([
        fetchApiErrors(filters, page, pageSize),
        fetchApiErrorStats(),
        fetchDistinctEndpoints(),
      ]);

      setErrors(errorsResult.data);
      setTotalCount(errorsResult.count);
      setStats(statsResult);
      setEndpoints(endpointsResult);
    } catch (err) {
      console.error("Failed to load API errors:", err);
    } finally {
      setLoading(false);
    }
  }, [endpointFilter, statusCodeFilter, dateFrom, dateTo, page]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [endpointFilter, statusCodeFilter, dateFrom, dateTo]);

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card padding="md">
          <div className="text-sm text-gray-500">Errors Today</div>
          <div className="text-2xl font-bold text-gray-900">
            {stats?.errorsToday ?? "-"}
          </div>
        </Card>
        <Card padding="md">
          <div className="text-sm text-gray-500">Errors This Week</div>
          <div className="text-2xl font-bold text-gray-900">
            {stats?.errorsThisWeek ?? "-"}
          </div>
        </Card>
        <Card padding="md">
          <div className="text-sm text-gray-500">Most Common Error</div>
          <div
            className="text-sm font-medium text-gray-900 truncate"
            title={stats?.topErrorMessage || ""}
          >
            {stats?.topErrorMessage
              ? stats.topErrorMessage.length > 60
                ? stats.topErrorMessage.substring(0, 60) + "..."
                : stats.topErrorMessage
              : "None"}
          </div>
        </Card>
        <Card padding="md">
          <div className="text-sm text-gray-500">
            Repeated Auth Failures (24h)
          </div>
          {stats?.repeatedAuthFailureIps &&
          stats.repeatedAuthFailureIps.length > 0 ? (
            <div className="text-sm">
              {stats.repeatedAuthFailureIps.map((item) => (
                <div key={item.ip} className="text-red-600 font-medium">
                  {item.ip}: {item.count} failures
                </div>
              ))}
            </div>
          ) : (
            <div className="text-2xl font-bold text-green-600">0</div>
          )}
        </Card>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Endpoint
          </label>
          <select
            value={endpointFilter}
            onChange={(e) => setEndpointFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Endpoints</option>
            {endpoints.map((ep) => (
              <option key={ep} value={ep}>
                {ep}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status Code
          </label>
          <select
            value={statusCodeFilter}
            onChange={(e) => setStatusCodeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All</option>
            <option value="400">400</option>
            <option value="401">401</option>
            <option value="403">403</option>
            <option value="404">404</option>
            <option value="405">405</option>
            <option value="429">429</option>
            <option value="500">500</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            From
          </label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            To
          </label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <button
          onClick={() => {
            setEndpointFilter("");
            setStatusCodeFilter("");
            setDateFrom("");
            setDateTo("");
          }}
          className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
        >
          Clear Filters
        </button>
      </div>

      {/* Results count */}
      <div className="mb-4 text-sm text-gray-500">
        {totalCount} error{totalCount !== 1 ? "s" : ""} found
      </div>

      {/* Error List */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="text-gray-500">Loading API errors...</div>
        </div>
      ) : errors.length === 0 ? (
        <Card padding="lg">
          <div className="text-center py-12">
            <p className="text-gray-500">No API errors found</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {errors.map((error) => (
            <Card
              key={error.id}
              padding="md"
              className={`border-l-4 ${error.status_code >= 500 ? "border-red-500" : "border-yellow-500"}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded ${
                        error.status_code >= 500
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {error.status_code}
                    </span>
                    <span className="text-xs font-mono text-gray-600">
                      {error.endpoint}
                    </span>
                    <span className="text-xs text-gray-400">
                      {error.method}
                    </span>
                    {error.auth_type && (
                      <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-600">
                        {error.auth_type}
                      </span>
                    )}
                    <span className="text-sm text-gray-500">
                      {formatDate(error.created_at)}{" "}
                      {formatTime(error.created_at)}
                    </span>
                  </div>

                  <div className="text-sm font-medium text-gray-900 mb-2">
                    {error.error_message}
                  </div>

                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    {error.ip_address && <span>IP: {error.ip_address}</span>}
                    {error.correlation_id && (
                      <span className="font-mono" title={error.correlation_id}>
                        ID: {error.correlation_id.substring(0, 8)}...
                      </span>
                    )}
                  </div>

                  {error.request_context &&
                    Object.keys(error.request_context).length > 0 && (
                      <details className="mt-2">
                        <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-900">
                          Request Context
                        </summary>
                        <pre className="mt-2 p-3 bg-gray-50 rounded text-xs overflow-x-auto">
                          {JSON.stringify(error.request_context, null, 2)}
                        </pre>
                      </details>
                    )}

                  {error.user_agent && (
                    <details className="mt-1">
                      <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">
                        User Agent
                      </summary>
                      <div className="mt-1 text-xs text-gray-500 break-all">
                        {error.user_agent}
                      </div>
                    </details>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// CLIENT ERRORS TAB (existing localStorage behavior)
// ============================================================================

const ClientErrorsTab: React.FC = () => {
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [filterType, setFilterType] = useState<string>("all");

  useEffect(() => {
    setErrors(getErrorLogs());
  }, []);

  const handleClearLogs = () => {
    if (confirm("Are you sure you want to clear all client error logs?")) {
      clearErrorLogs();
      setErrors([]);
    }
  };

  const handleExportLogs = () => {
    const dataStr = JSON.stringify(errors, null, 2);
    const dataUri =
      "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
    const exportFileDefaultName = `client-error-logs-${new Date().toISOString()}.json`;

    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute("download", exportFileDefaultName);
    linkElement.click();
  };

  const filteredErrors =
    filterType === "all" ? errors : errors.filter((e) => e.type === filterType);

  const errorTypes = Array.from(new Set(errors.map((e) => e.type)));

  return (
    <div>
      {/* Filters and Actions */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">
            Filter by type:
          </label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Types</option>
            {errorTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleExportLogs}
            disabled={errors.length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            Export JSON
          </button>
          <button
            onClick={handleClearLogs}
            disabled={errors.length === 0}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            Clear Logs
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card padding="md">
          <div className="text-sm text-gray-500">Total Errors</div>
          <div className="text-2xl font-bold text-gray-900">
            {errors.length}
          </div>
        </Card>
        <Card padding="md">
          <div className="text-sm text-gray-500">Error Types</div>
          <div className="text-2xl font-bold text-gray-900">
            {errorTypes.length}
          </div>
        </Card>
        <Card padding="md">
          <div className="text-sm text-gray-500">Filtered Results</div>
          <div className="text-2xl font-bold text-gray-900">
            {filteredErrors.length}
          </div>
        </Card>
      </div>

      {/* Error List */}
      {filteredErrors.length === 0 ? (
        <Card padding="lg">
          <div className="text-center py-12">
            <p className="text-gray-500">No client error logs found</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredErrors.map((error, index) => (
            <Card
              key={index}
              padding="md"
              className="border-l-4 border-red-500"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-800">
                      {error.type}
                    </span>
                    <span className="text-sm text-gray-500">
                      {formatDate(error.timestamp)}{" "}
                      {formatTime(error.timestamp)}
                    </span>
                  </div>

                  <div className="text-sm font-medium text-gray-900 mb-2">
                    {error.error}
                  </div>

                  {error.stack && (
                    <details className="mt-2">
                      <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-900">
                        Stack Trace
                      </summary>
                      <pre className="mt-2 p-3 bg-gray-50 rounded text-xs overflow-x-auto">
                        {error.stack}
                      </pre>
                    </details>
                  )}

                  {error.additionalData &&
                    Object.keys(error.additionalData).length > 0 && (
                      <details className="mt-2">
                        <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-900">
                          Context
                        </summary>
                        <pre className="mt-2 p-3 bg-gray-50 rounded text-xs overflow-x-auto">
                          {JSON.stringify(error.additionalData, null, 2)}
                        </pre>
                      </details>
                    )}

                  {error.userAgent && (
                    <div className="mt-2 text-xs text-gray-500">
                      User Agent: {error.userAgent}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// ALERT RULES TAB
// ============================================================================

const KNOWN_FUNCTIONS = [
  "generate-offer",
  "zuora-webhook",
  "sendgrid-webhook",
  "check-member-status",
  "invite-user",
  "verify-invitation-code",
  "send-email",
  "list-events",
  "public-content",
  "chat-rag",
  "saved-content",
  "geocode",
  "create-support-task",
  "evaluate-alerts",
  "process-messages",
  "regenerate-voucher-images",
];

const METRIC_OPTIONS = [
  { value: "error_rate", label: "Error Rate %", unit: "%" },
  { value: "avg_response_ms", label: "Avg Response ms", unit: "ms" },
  { value: "no_calls_hours", label: "No Calls Hours", unit: "h" },
  { value: "webhook_silence_hours", label: "Webhook Silence Hours", unit: "h" },
];

interface RuleFormData {
  name: string;
  metric: string;
  function_name: string;
  threshold: string;
  severity: string;
  evaluation_window_minutes: string;
  cooldown_minutes: string;
  notify_email: boolean;
  notification_email: string;
}

const emptyForm: RuleFormData = {
  name: "",
  metric: "error_rate",
  function_name: KNOWN_FUNCTIONS[0],
  threshold: "",
  severity: "warning",
  evaluation_window_minutes: "60",
  cooldown_minutes: "60",
  notify_email: true,
  notification_email: "",
};

const AlertRulesTab: React.FC = () => {
  const { user } = useAuth();
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [activeAlerts, setActiveAlerts] = useState<SystemAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [evaluating, setEvaluating] = useState(false);
  const [evalResults, setEvalResults] = useState<EvaluationResult[] | null>(
    null,
  );
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null);
  const [form, setForm] = useState<RuleFormData>(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([
        fetchAlertRules(),
        fetchActiveAlerts(),
      ]);
      setRules(r);
      setActiveAlerts(a);
    } catch (err) {
      console.error("Failed to load alert data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEvaluate = async () => {
    setEvaluating(true);
    setEvalResults(null);
    try {
      const results = await triggerAlertEvaluation();
      setEvalResults(results);
      await loadData();
    } catch (err) {
      console.error("Evaluation failed:", err);
    } finally {
      setEvaluating(false);
    }
  };

  const handleAcknowledge = async (id: string) => {
    if (!user) {
      return;
    }
    try {
      await acknowledgeAlert(id, user.id);
      await loadData();
    } catch (err) {
      console.error("Failed to acknowledge:", err);
    }
  };

  const handleResolve = async (id: string) => {
    try {
      await resolveAlert(id);
      await loadData();
    } catch (err) {
      console.error("Failed to resolve:", err);
    }
  };

  const handleToggleEnabled = async (rule: AlertRule) => {
    try {
      await updateAlertRule(rule.id, { enabled: !rule.enabled });
      await loadData();
    } catch (err) {
      console.error("Failed to toggle rule:", err);
    }
  };

  const handleDelete = async (rule: AlertRule) => {
    if (!confirm(`Delete rule "${rule.name}"?`)) {
      return;
    }
    try {
      await deleteAlertRule(rule.id);
      await loadData();
    } catch (err) {
      console.error("Failed to delete rule:", err);
    }
  };

  const openEditForm = (rule: AlertRule) => {
    setEditingRule(rule);
    setForm({
      name: rule.name,
      metric: rule.metric,
      function_name: rule.function_name || KNOWN_FUNCTIONS[0],
      threshold: String(rule.threshold),
      severity: rule.severity,
      evaluation_window_minutes: String(rule.evaluation_window_minutes),
      cooldown_minutes: String(rule.cooldown_minutes),
      notify_email: rule.notify_email,
      notification_email: rule.notification_email || "",
    });
    setShowForm(true);
  };

  const openCreateForm = () => {
    setEditingRule(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.threshold) {
      return;
    }
    setSaving(true);
    try {
      const input: AlertRuleInput = {
        name: form.name,
        metric: form.metric,
        function_name: form.function_name,
        threshold: parseFloat(form.threshold),
        severity: form.severity,
        evaluation_window_minutes: parseInt(form.evaluation_window_minutes, 10),
        cooldown_minutes: parseInt(form.cooldown_minutes, 10),
        notify_email: form.notify_email,
        notification_email: form.notification_email || null,
      };

      if (editingRule) {
        await updateAlertRule(editingRule.id, input);
      } else {
        await createAlertRule(input);
      }
      setShowForm(false);
      await loadData();
    } catch (err) {
      console.error("Failed to save rule:", err);
    } finally {
      setSaving(false);
    }
  };

  const metricUnit = (metric: string) =>
    METRIC_OPTIONS.find((m) => m.value === metric)?.unit || "";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-gray-500">Loading alert rules...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Active Alerts Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
            Active Alerts ({activeAlerts.length})
          </h3>
          <button
            onClick={handleEvaluate}
            disabled={evaluating}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {evaluating ? "Evaluating..." : "Run Evaluation Now"}
          </button>
        </div>

        {/* Evaluation Results */}
        {evalResults && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="text-sm font-medium text-blue-900 mb-1">
              Evaluation Complete
            </div>
            <div className="text-xs text-blue-700 space-x-4">
              <span>
                Fired: {evalResults.filter((r) => r.action === "fired").length}
              </span>
              <span>
                Resolved:{" "}
                {evalResults.filter((r) => r.action === "resolved").length}
              </span>
              <span>
                OK: {evalResults.filter((r) => r.action === "ok").length}
              </span>
              <span>
                Cooldown:{" "}
                {
                  evalResults.filter((r) => r.action === "skipped_cooldown")
                    .length
                }
              </span>
            </div>
          </div>
        )}

        {activeAlerts.length === 0 ? (
          <Card padding="md">
            <div className="text-center py-6 text-sm text-gray-500">
              No active alerts
            </div>
          </Card>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">
                    Severity
                  </th>
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">
                    Message
                  </th>
                  <th className="text-right py-2 px-3 text-gray-500 font-medium">
                    Value
                  </th>
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">
                    Fired
                  </th>
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">
                    Status
                  </th>
                  <th className="text-right py-2 px-3 text-gray-500 font-medium">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {activeAlerts.map((alert) => (
                  <tr
                    key={alert.id}
                    className={`border-b border-gray-100 ${
                      alert.severity === "critical"
                        ? "bg-red-50"
                        : "bg-yellow-50"
                    }`}
                  >
                    <td className="py-2 px-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          alert.severity === "critical"
                            ? "bg-red-100 text-red-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {alert.severity}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-gray-900">{alert.message}</td>
                    <td className="py-2 px-3 text-right font-mono text-gray-900">
                      {alert.current_value}
                    </td>
                    <td className="py-2 px-3 text-gray-500 text-xs">
                      {timeAgo(alert.created_at)}
                    </td>
                    <td className="py-2 px-3">
                      <span
                        className={`text-xs ${
                          alert.status === "acknowledged"
                            ? "text-blue-600"
                            : "text-red-600"
                        }`}
                      >
                        {alert.status}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right space-x-2">
                      {alert.status === "active" && (
                        <button
                          onClick={() => handleAcknowledge(alert.id)}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          Acknowledge
                        </button>
                      )}
                      <button
                        onClick={() => handleResolve(alert.id)}
                        className="text-xs text-green-600 hover:text-green-800"
                      >
                        Resolve
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Alert Rules Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
            Alert Rules ({rules.length})
          </h3>
          <button
            onClick={openCreateForm}
            className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Add Rule
          </button>
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <Card padding="md" className="mb-6 border-2 border-blue-200">
            <h4 className="text-sm font-semibold text-gray-900 mb-4">
              {editingRule ? "Edit Rule" : "New Alert Rule"}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. generate-offer high error rate"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Function
                </label>
                <select
                  value={form.function_name}
                  onChange={(e) =>
                    setForm({ ...form, function_name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {KNOWN_FUNCTIONS.map((fn) => (
                    <option key={fn} value={fn}>
                      {fn}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Metric
                </label>
                <select
                  value={form.metric}
                  onChange={(e) => setForm({ ...form, metric: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {METRIC_OPTIONS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Threshold ({metricUnit(form.metric)})
                </label>
                <input
                  type="number"
                  value={form.threshold}
                  onChange={(e) =>
                    setForm({ ...form, threshold: e.target.value })
                  }
                  placeholder="e.g. 5"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Severity
                </label>
                <select
                  value={form.severity}
                  onChange={(e) =>
                    setForm({ ...form, severity: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="warning">Warning</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Eval Window (min)
                </label>
                <input
                  type="number"
                  value={form.evaluation_window_minutes}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      evaluation_window_minutes: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cooldown (min)
                </label>
                <input
                  type="number"
                  value={form.cooldown_minutes}
                  onChange={(e) =>
                    setForm({ ...form, cooldown_minutes: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-center gap-4 pt-6">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.notify_email}
                    onChange={(e) =>
                      setForm({ ...form, notify_email: e.target.checked })
                    }
                    className="rounded border-gray-300"
                  />
                  Email notifications
                </label>
              </div>
              {form.notify_email && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notification Emails (optional)
                  </label>
                  <input
                    type="text"
                    value={form.notification_email}
                    onChange={(e) =>
                      setForm({ ...form, notification_email: e.target.value })
                    }
                    placeholder="Comma-separated, e.g. admin@co.com, ops@co.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Leave blank for your email. Separate multiple addresses with
                    commas.
                  </p>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={handleSave}
                disabled={saving || !form.name || !form.threshold}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {saving
                  ? "Saving..."
                  : editingRule
                    ? "Update Rule"
                    : "Create Rule"}
              </button>
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
            </div>
          </Card>
        )}

        {/* Rules Table */}
        {rules.length === 0 ? (
          <Card padding="md">
            <div className="text-center py-6 text-sm text-gray-500">
              No alert rules configured
            </div>
          </Card>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">
                    Enabled
                  </th>
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">
                    Name
                  </th>
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">
                    Function
                  </th>
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">
                    Metric
                  </th>
                  <th className="text-right py-2 px-3 text-gray-500 font-medium">
                    Threshold
                  </th>
                  <th className="text-left py-2 px-3 text-gray-500 font-medium">
                    Severity
                  </th>
                  <th className="text-right py-2 px-3 text-gray-500 font-medium">
                    Window
                  </th>
                  <th className="text-right py-2 px-3 text-gray-500 font-medium">
                    Cooldown
                  </th>
                  <th className="text-right py-2 px-3 text-gray-500 font-medium">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr
                    key={rule.id}
                    className={`border-b border-gray-100 ${!rule.enabled ? "opacity-50" : ""}`}
                  >
                    <td className="py-2 px-3">
                      <button
                        onClick={() => handleToggleEnabled(rule)}
                        className={`w-8 h-5 rounded-full transition-colors ${
                          rule.enabled ? "bg-green-500" : "bg-gray-300"
                        } relative`}
                      >
                        <span
                          className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                            rule.enabled ? "translate-x-3.5" : "translate-x-0.5"
                          }`}
                        />
                      </button>
                    </td>
                    <td className="py-2 px-3 text-gray-900">
                      {rule.name}
                      {rule.is_default && (
                        <span className="ml-2 text-xs text-gray-400">
                          (default)
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 font-mono text-gray-600 text-xs">
                      {rule.function_name}
                    </td>
                    <td className="py-2 px-3 text-gray-600">
                      {METRIC_OPTIONS.find((m) => m.value === rule.metric)
                        ?.label || rule.metric}
                    </td>
                    <td className="py-2 px-3 text-right font-mono text-gray-900">
                      {rule.threshold}
                      {metricUnit(rule.metric)}
                    </td>
                    <td className="py-2 px-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          rule.severity === "critical"
                            ? "bg-red-100 text-red-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {rule.severity}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right text-gray-500">
                      {rule.evaluation_window_minutes}m
                    </td>
                    <td className="py-2 px-3 text-right text-gray-500">
                      {rule.cooldown_minutes}m
                    </td>
                    <td className="py-2 px-3 text-right space-x-2">
                      <button
                        onClick={() => openEditForm(rule)}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(rule)}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN PAGE
// ============================================================================

const ErrorLogs: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>("health");

  const tabs: { id: Tab; name: string }[] = [
    { id: "health", name: "System Health" },
    { id: "api", name: "API Errors" },
    { id: "client", name: "Client Errors" },
    { id: "alerts", name: "Alert Rules" },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Operations</h1>
        <p className="text-gray-600 mt-1">
          Edge function health, API errors, client diagnostics, and alerting
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm
                ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }
              `}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "health" && (
        <>
          <SmsRetentionPanel />
          <SystemHealthTab />
        </>
      )}
      {activeTab === "api" && <ApiErrorsTab />}
      {activeTab === "client" && <ClientErrorsTab />}
      {activeTab === "alerts" && <AlertRulesTab />}
    </div>
  );
};

export default ErrorLogs;
